import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import PropTypes from 'prop-types';

import { Column } from 'primereact/column';
import { differenceInDays } from 'date-fns';
import ButtonSp from '../../components/ButtonSp';

import InputTextSp from '../../components/InputTextSp';

import LabelSp from '../../components/LabelSp';
import { showMessage } from '../../components/MessageDialog';

import ClienteService from '../../services/ClienteService';

import {
  cloneObj,
  errorHandle,
  isDate,
  newDateOnly,
  strToDate,
  validCPF,
} from '../../util/functions';
import { ClienteContatoEndereco, ClienteModel, ClienteSocioModel } from '../../util/Models';
import { StateScreen } from '../constants';
import DropdownSp from '../../components/DropdownSp';
import InputMaskSp from '../../components/InputMaskSp';
import InputTextareaSp from '../../components/InputTextareaSp';
import DataTableSp from '../../components/DataTableSp';
import BotaoMenuGrid from '../../components/BotaoMenuGrid';

import { listaUfs } from '../../config/Constantes';
import DropDownLazy from '../../components/DropdownLazy';

import AuthService from '../../services/AuthService';
import RevendaService from '../../services/RevendaService';
import MunicipioService from '../../services/MunicipioService';

import DispositivoService from '../../services/DispositivoService';
import SelectButtons from '../../components/SelectButtons';
import ContratoOrcamento from '../../components/ContratoOrcamento/contrato';
import SistemaService from '../../services/SistemaService';

function ClienteCrud(props) {
  ClienteCrud.propTypes = {
    stateScreen: PropTypes.string.isRequired,
    idSelected: PropTypes.number.isRequired,
    onClose: PropTypes.func.isRequired,
  };

  ClienteCrud.defaultProps = {
    messageConfirmation: 'Confirma os dados',
    showConfirmation: false,
  };

  const { stateScreen, idSelected, onClose } = props;

  const revendaUsuario = AuthService.getUsuario().revenda
    ? {
        label: AuthService.getUsuario().revenda.nome,
        value: AuthService.getUsuario().revenda.id,
      }
    : null;

  // states
  const [cliente, setCliente] = useState(new ClienteModel());
  const [ufSelecionada, setUfSelecionada] = useState('MG');

  const [revendaSelecionada, setRevendaSelecionada] = useState();
  const [clientePrincipalSelecionado, setClientePrincipalSelecionado] = useState();
  const [municipioSelecionado, setMunicipioSelecionado] = useState();
  const [clienteSocioEdicao, setClienteSocioEdicao] = useState();
  const [editandoClienteSocio, setEditandoClienteSocio] = useState(false);
  const [paginaSelecionada, setPaginaSelecionada] = useState(0);
  const [valorOriginal, setValorOriginal] = useState(0);
  const [valorCalculadoOriginal, setValorCalculadoOriginal] = useState(0);

  const [cpf, setCpf] = useState('');
  const [cnpj, setCnpj] = useState('');

  const [errorLoadRecord, setErrorLoadRecord] = useState(false);
  const [dispositivosUsados, setDispositivosUsados] = useState('');

  // useCallbacks

  const loadRecord = useCallback(async _id => {
    try {
      const retorno = await ClienteService.findById(_id);
      setValorOriginal(retorno.percentualSalario);
      setValorCalculadoOriginal(retorno.percentualSalarioCalculado);

      const municipio = retorno.clienteContatoEndereco
        ? {
            label: retorno.clienteContatoEndereco.municipio.nome,
            value: retorno.clienteContatoEndereco.municipio.id,
          }
        : null;

      if (!retorno.clienteContatoEndereco) {
        retorno.clienteContatoEndereco = new ClienteContatoEndereco();
      }

      const revenda = retorno.revenda
        ? {
            label: retorno.revenda.nome,
            value: retorno.revenda.id,
          }
        : null;

      if (isDate(retorno.dataInicioContrato)) {
        retorno.dataInicioContrato = strToDate(retorno.dataInicioContrato);
      }

      if (isDate(retorno.dataInclusao)) {
        retorno.dataInclusao = strToDate(retorno.dataInclusao);
      }

      if (isDate(retorno.dataFimContrato)) {
        retorno.dataFimContrato = strToDate(retorno.dataFimContrato);
      }

      if (retorno.numeroContrato) {
        const chaveDispositivos = await DispositivoService.findAll({
          numeroContrato: retorno.numeroContrato,
        });

        setDispositivosUsados(chaveDispositivos.items.length);
      }

      const clientePrincipal = retorno.clientePrincipal
        ? {
            label: `${retorno.clientePrincipal.numeroContrato} - ${retorno.clientePrincipal.nome}`,
            value: retorno.clientePrincipal.id,
          }
        : null;

      setMunicipioSelecionado(municipio);
      setRevendaSelecionada(revenda);
      setClientePrincipalSelecionado(clientePrincipal);
      setCliente(retorno);

      if (retorno.tipoPessoa === 'F') {
        setCpf(retorno.cpfCnpj);
        setCnpj('');
      } else {
        setCpf('');
        setCnpj(retorno.cpfCnpj);
      }

      setErrorLoadRecord(false);
    } catch (err) {
      setErrorLoadRecord(true);
      errorHandle(err);
    }
  }, []);

  // cliente que paga, quando o mesmo CNPJ tem mais de um cadastro (sistema real e fiscal)
  const loadClientePrincipal = useCallback(
    async _nome => {
      if (_nome !== undefined) {
        const r = await ClienteService.findAll({ nome: _nome, resumo: '', limit: 50 });

        return r.items
          .filter(e => Number(e.id) !== Number(cliente.id))
          .map(e => {
            return {
              label: `${e.numeroContrato} - ${e.nome}`,
              value: e.id,
            };
          });
      }
      return [];
    },
    [cliente.id]
  );

  const loadRevenda = useCallback(async _nome => {
    if (_nome !== undefined) {
      const r = await RevendaService.findAll({ nome: _nome, limit: 50 });

      const retorno = r.items.map(e => {
        return {
          label: e.nome,
          value: e.id,
        };
      });
      return retorno;
    }
    return [];
  }, []);

  const loadMunicipio = useCallback(async (_nome, uf) => {
    if (_nome !== undefined) {
      const r = await MunicipioService.findAll({ nome: _nome, uf, limit: 50 });

      const retorno = r.items.map(e => {
        return {
          label: e.nome,
          value: e.id,
        };
      });
      return retorno;
    }
    return [];
  }, []);

  // funcoes
  function viewMode() {
    return stateScreen === StateScreen.stView || errorLoadRecord;
  }

  function permissaoAdminAlteracao() {
    if (AuthService.isPerfilGerente()) {
      return true;
    }
    const dataAtual = newDateOnly();
    const dataInclusao = new Date(cliente.dataInclusao) || new Date();
    const dias = differenceInDays(dataAtual, dataInclusao);

    return dias < 2;
  }

  function bloqueiaRevenda() {
    return AuthService.getUsuario().idRevenda && !AuthService.isPerfilGerente();
  }

  function permissaoModulos() {
    if (cliente && cliente.chave && cliente.id && !AuthService.isPerfilGerente()) {
      return false;
    }
    return true;
  }

  function novoSocio() {
    setClienteSocioEdicao(new ClienteSocioModel());
    setEditandoClienteSocio(false);
  }

  function alterarSocio(clienteSocio) {
    setClienteSocioEdicao(cloneObj(clienteSocio));
    setEditandoClienteSocio(true);
  }

  function excluirSocio(clienteSocio) {
    showMessage('Confirmação', 'Confirma Exclusão do Sócio?', idx => {
      if (idx === 1) {
        let idArray = -1;
        const { socios } = cliente;
        socios.forEach((e, i) => {
          if (clienteSocio.cpf === e.cpf) {
            idArray = i;
          }
        });

        if (idArray >= 0) {
          socios.splice(idArray, 1);
          setCliente({ ...cliente, socios });
        }
      }
    });
  }

  function salvarSocio() {
    if (!clienteSocioEdicao || !clienteSocioEdicao.nome) {
      toast.error('Informe o nome do sócio.');
      return;
    }

    if (!clienteSocioEdicao.email) {
      toast.error('Informe o e-mail do sócio.');
      return;
    }

    if (!validCPF(clienteSocioEdicao.cpf)) {
      toast.error('Informe o CPF do sócio inválido.');
      return;
    }

    let idArray = -1;
    cliente.socios.forEach((e, idx) => {
      if (clienteSocioEdicao.cpf === e.cpf) {
        idArray = idx;
      }
    });

    if (!editandoClienteSocio && idArray >= 0) {
      toast.error('Sócio já foi lançado.');
    } else {
      if (idArray >= 0) {
        cliente.socios[idArray] = clienteSocioEdicao;
      } else {
        cliente.socios.push(clienteSocioEdicao);
      }

      novoSocio();
    }
  }

  function handleBack() {
    if (stateScreen === StateScreen.stView) {
      onClose();
    } else {
      showMessage('Confirmação', 'Abandonar mudanças?', idx => {
        if (idx === 1) {
          onClose();
        }
      });
    }
  }

  function validateRecord() {
    if (cliente.tipoPessoa === 'F' && !cpf) {
      toast.error('Informe o CPF');
      return false;
    }

    if (cliente.tipoPessoa === 'J' && !cnpj) {
      toast.error('Informe o CNPJ');
      return false;
    }

    if (!cliente.idRevenda) {
      toast.error('Informe a Revenda');
      return false;
    }

    if (!cliente.nome) {
      toast.error('Informe o nome');
      return false;
    }

    if (!cliente.nomeFantasia) {
      toast.error('Informe o Nome Fantasia');
      return false;
    }

    if (!cliente.clienteContatoEndereco.logradouro) {
      toast.error('Informe o logradouro');
      return false;
    }

    if (!cliente.clienteContatoEndereco.numero) {
      toast.error('Informe o número');
      return false;
    }

    if (!cliente.clienteContatoEndereco.bairro) {
      toast.error('Informe o bairro');
      return false;
    }

    if (!cliente.clienteContatoEndereco.idMunicipio) {
      toast.error('Informe o município');
      return false;
    }

    if (!cliente.clienteContatoEndereco.nomeContato) {
      toast.error('Informe o contato');
      return false;
    }

    if (!cliente.clienteContatoEndereco.email) {
      toast.error('Informe o email');
      return false;
    }

    if (cliente.socios.length === 0) {
      toast.error('Informe o(s) sócio(s)');
      return false;
    }

    if (!cliente.numeroDispositivos) {
      toast.error('Informe número de dispositívos');
      setPaginaSelecionada(1);
      return false;
    }

    if (!cliente.diaVencimento) {
      toast.error('Informe o dia de vencimento');
      setPaginaSelecionada(1);
      return false;
    }

    if (!cliente.percentualSalarioCalculado) {
      toast.error('É necessário calcular o % salário do contrato');
      setPaginaSelecionada(1);
      return false;
    }
    const adm = AuthService.isPerfilGerente();

    if (
      (valorOriginal !== cliente.percentualSalario ||
        cliente.percentualSalarioCalculado !== valorCalculadoOriginal) &&
      cliente.percentualSalarioCalculado > cliente.percentualSalario
    ) {
      if (!adm && 1 - cliente.percentualSalario / cliente.percentualSalarioCalculado > 0.05) {
        toast.error('Desconto máximo de 5% ultrapassado.');
        setPaginaSelecionada(1);
        return false;
      }
    }

    if (cliente.inativo && !cliente.dataFimContrato) {
      toast.error('Informe a data do final do contrato');
      setPaginaSelecionada(1);
      return false;
    }

    return true;
  }

  function handleSave() {
    if (!validateRecord()) {
      return;
    }

    showMessage('Confirmação', 'Confirma os dados?', idx => {
      if (idx === 1) {
        if (stateScreen === StateScreen.stView) {
          onClose();
        } else {
          salvarRecord();
        }
      }
    });
  }

  async function salvarRecord() {
    try {
      let retorno;
      if (cliente.tipoPessoa === 'F') {
        cliente.cpfCnpj = cpf;
      } else {
        cliente.cpfCnpj = cnpj;
      }
      if (stateScreen === StateScreen.stInsert) {
        retorno = await ClienteService.insert(cliente);
      } else {
        retorno = await ClienteService.update(cliente);
      }
      toast.success('Registro salvo com sucesso.');
      onClose(retorno);
    } catch (err) {
      errorHandle(err);
    }
  }

  // useEffects
  useEffect(() => {
    if (stateScreen === StateScreen.stUpdate || stateScreen === StateScreen.stView) {
      loadRecord(idSelected);
      if (stateScreen === StateScreen.stUpdate) {
        setTimeout(() => {
          verificaPermissao();
        }, 500);
      }
    } else if (stateScreen === StateScreen.stInsert) {
      const carregaCliente = async () => {
        const novo = new ClienteModel();
        novo.valorSalarioBase = AuthService.getConfiguracao()?.valorSalarioVigente;
        if (AuthService.getUsuario().idRevenda) {
          novo.idRevenda = AuthService.getUsuario().idRevenda;
          novo.revenda = AuthService.getUsuario().revenda;
        }

        const sistemaErp = await SistemaService.findById(1);
        if (sistemaErp) {
          novo.sistema = sistemaErp;
          novo.idSistema = sistemaErp.id;
        }

        setCpf('');
        setCnpj('');

        setCliente(novo);
        setRevendaSelecionada(revendaUsuario);
      };
      carregaCliente();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadRecord, idSelected, stateScreen]);

  const verificaPermissao = useCallback(() => {
    if (AuthService.isPerfilGerente()) {
      return true;
    }
    if (!cliente) {
      return true;
    }
    const dataAtual = newDateOnly();
    const dataInclusao = new Date(cliente.dataInclusao) || new Date();
    const dias = differenceInDays(dataAtual, dataInclusao);

    if (dias > 1) {
      toast.error('Você não tem permissão para alteração com mais de 1 dia do lançamento');
      setTimeout(() => {
        onClose();
      }, 3000);
    }
  }, [cliente, onClose]);

  const renderPaginaSelecionada = () => {
    if (paginaSelecionada === 0) {
      return renderCadastro();
    }
    if (paginaSelecionada === 1) {
      return renderContrato();
    }
  };
  const buttons = ['Cadastro', 'Contrato'];

  const consultaCepj = async () => {
    try {
      // 20.082.225/0001-41
      const cepConsulta = cliente.clienteContatoEndereco.cep.replace(/[^\d]/g, '');
      if (!cepConsulta) {
        toast.error('CEP Inválido');
        return;
      }
      const enderecoConsulta = await ClienteService.consultaCEP(cepConsulta);
      if (enderecoConsulta) {
        const { clienteContatoEndereco } = cliente;
        clienteContatoEndereco.logradouro = enderecoConsulta.logradouro;
        clienteContatoEndereco.bairro = enderecoConsulta.bairro;
        clienteContatoEndereco.cep = enderecoConsulta.cep;

        if (enderecoConsulta.municipio) {
          clienteContatoEndereco.idMunicipio = enderecoConsulta.municipio.id;
          setMunicipioSelecionado({
            value: enderecoConsulta.municipio.id,
            label: enderecoConsulta.municipio.nome,
          });
        }

        clienteContatoEndereco.logradouro = enderecoConsulta.logradouro;

        setCliente({
          ...cliente,

          clienteContatoEndereco: { ...clienteContatoEndereco },
        });
      }
    } catch (err) {
      errorHandle(err);
    }
  };
  const consultaCnpj = async () => {
    try {
      // 20.082.225/0001-41
      const cnpjConsulta = cnpj.replace(/[^\d]/g, '');
      if (!cnpjConsulta) {
        toast.error('CNPJ Inválido');
        return;
      }
      const clienteConsulta = await ClienteService.consultaCNPJ(cnpjConsulta);
      if (clienteConsulta) {
        const { clienteContatoEndereco } = cliente;
        clienteContatoEndereco.logradouro = clienteConsulta.logradouro;
        clienteContatoEndereco.bairro = clienteConsulta.bairro;
        clienteContatoEndereco.cep = clienteConsulta.cep;
        clienteContatoEndereco.email = clienteConsulta.email;
        clienteContatoEndereco.fone = clienteConsulta.fone;
        clienteContatoEndereco.celular = clienteConsulta.celular1;
        clienteContatoEndereco.numero = clienteConsulta.numero;
        if (clienteConsulta.municipio) {
          clienteContatoEndereco.idMunicipio = clienteConsulta.municipio.id;
          setMunicipioSelecionado({
            value: clienteConsulta.municipio.id,
            label: clienteConsulta.municipio.nome,
          });
        }

        clienteContatoEndereco.logradouro = clienteConsulta.logradouro;

        setCliente({
          ...cliente,
          nome: clienteConsulta.nome,
          nomeFantasia: clienteConsulta.nomeFantasia,

          clienteContatoEndereco: { ...clienteContatoEndereco },
        });
      }
    } catch (err) {
      errorHandle(err);
    }
  };

  // render principal
  return (
    <>
      <div className="p-col-12 p-lg-12" style={{ padding: 0, margin: 0 }}>
        <SelectButtons
          widthButton={200}
          buttons={buttons}
          selectedIndex={paginaSelecionada}
          onClick={itemIndex => setPaginaSelecionada(itemIndex)}
        />
      </div>
      {renderPaginaSelecionada()}
    </>
  );

  function renderCadastro() {
    return (
      <>
        <>
          <div className="p-col-3 p-md-2 p-lg-2 p-fluid">
            <LabelSp>Contrato</LabelSp>
            <InputTextSp disabled value={cliente.numeroContrato} />
          </div>

          <div className="p-col-9 p-md-6 p-lg-6 p-fluid" style={{ margin: 0, padding: 0 }}>
            <div className="p-grid" style={{ margin: 0, padding: 0 }}>
              <div className="p-col-5 p-fluid">
                <LabelSp>Tp. Pessoa</LabelSp>
                <DropdownSp
                  autoFocus
                  value={cliente.tipoPessoa}
                  name="cmbTipoPessoa"
                  disabled={viewMode() || !permissaoAdminAlteracao()}
                  options={[
                    { label: 'Física', value: 'F' },
                    { label: 'Jurídica', value: 'J' },
                  ]}
                  onChange={e => {
                    setCliente({ ...cliente, tipoPessoa: e.target.value });
                  }}
                />
              </div>

              {cliente.tipoPessoa === 'F' ? (
                <div className="p-col-7 p-fluid">
                  <LabelSp>CPF</LabelSp>
                  <InputMaskSp
                    required
                    name="txtCPF"
                    appAutofocus
                    mask="999.999.999-99"
                    value={cpf}
                    disabled={viewMode() || !permissaoAdminAlteracao()}
                    onChange={e => {
                      setCpf(e.target.value);
                    }}
                  />
                </div>
              ) : (
                <div className="p-col-7 p-fluid" style={{ margin: 0, padding: 0 }}>
                  <div style={{ margin: 0, padding: 0, display: 'flex' }}>
                    <div style={{ margin: 0, padding: 3, flex: 1 }}>
                      <LabelSp>CNPJ</LabelSp>
                      <InputMaskSp
                        mask="99.999.999/9999-99"
                        required
                        value={cnpj}
                        disabled={viewMode() || !permissaoAdminAlteracao()}
                        onChange={e => {
                          setCnpj(e.target.value);
                        }}
                      />
                    </div>
                    <div style={{ margin: 0, padding: 3, paddingRight: 0, paddingLeft: 0 }}>
                      <LabelSp style={{ display: 'block' }}>&#160;</LabelSp>
                      <ButtonSp
                        icon="pi pi-search"
                        title="Consulta CNPJ"
                        onClick={consultaCnpj}
                        disabled={viewMode() || !permissaoAdminAlteracao()}
                        type="button"
                        className="p-button-secondary"
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="p-col-12 p-md-4 p-lg-4 p-fluid">
            <LabelSp>Revenda</LabelSp>
            <DropDownLazy
              autoLoad
              placeholder="Selecione"
              required
              value={revendaSelecionada}
              onChange={e => {
                setRevendaSelecionada(e);
                const revenda = e ? { id: e.value, nome: e.label } : null;
                setCliente({
                  ...cliente,
                  revenda,
                  idRevenda: e?.value,
                });
              }}
              onFilter={async txtFilter => {
                const retorno = await loadRevenda(txtFilter);
                return retorno;
              }}
              disabled={viewMode() || bloqueiaRevenda() || !permissaoAdminAlteracao()}
            />
          </div>

          <div className="p-col-12 p-md-5 p-lg-5 p-fluid">
            <LabelSp>Razão Social</LabelSp>
            <InputTextSp
              value={cliente.nome}
              disabled={viewMode() || !permissaoAdminAlteracao()}
              maxLength={100}
              required
              onChange={e => {
                setCliente({ ...cliente, nome: e.target.value });
              }}
            />
          </div>

          <div className="p-col-12 p-md-4 p-lg-4 p-fluid">
            <LabelSp>Fantasia</LabelSp>
            <InputTextSp
              value={cliente.nomeFantasia}
              maxLength={100}
              required
              disabled={viewMode() || !permissaoAdminAlteracao()}
              onChange={e => {
                setCliente({ ...cliente, nomeFantasia: e.target.value });
              }}
            />
          </div>

          <div className="p-col-12 p-md-3 p-lg-3 p-fluid">
            <LabelSp>Insc. Estadual</LabelSp>
            <InputTextSp
              value={cliente.inscricaoEstadual}
              disabled={viewMode() || !permissaoAdminAlteracao()}
              maxLength={20}
              onChange={e => {
                setCliente({ ...cliente, inscricaoEstadual: e.target.value });
              }}
            />
          </div>

          <div className="p-col-12 p-md-2 p-lg-2 p-fluid" style={{ margin: 0, padding: 0 }}>
            <div style={{ margin: 0, padding: 0, display: 'flex' }}>
              <div style={{ margin: 0, padding: 3, flex: 1 }}>
                <LabelSp>CEP</LabelSp>
                <InputMaskSp
                  name="txtCep"
                  mask="99999-999"
                  disabled={viewMode() || !permissaoAdminAlteracao()}
                  value={cliente.clienteContatoEndereco?.cep}
                  onChange={e => {
                    const { clienteContatoEndereco } = cliente;
                    clienteContatoEndereco.cep = e.target.value;
                    setCliente({
                      ...cliente,
                      clienteContatoEndereco: { ...clienteContatoEndereco },
                    });
                  }}
                />
              </div>

              <div style={{ margin: 0, padding: 3, paddingRight: 0, paddingLeft: 0 }}>
                <LabelSp style={{ display: 'block' }}>&#160;</LabelSp>
                <ButtonSp
                  icon="pi pi-search"
                  title="Consulta CEP"
                  onClick={consultaCepj}
                  disabled={viewMode() || !permissaoAdminAlteracao()}
                  type="button"
                  className="p-button-secondary"
                />
              </div>
            </div>
          </div>

          <div className="p-col-9 p-md-5 p-lg-5 p-fluid">
            <LabelSp>Endereço</LabelSp>
            <InputTextSp
              required
              maxLength={100}
              value={cliente.clienteContatoEndereco?.logradouro}
              disabled={viewMode() || !permissaoAdminAlteracao()}
              onChange={e => {
                const { clienteContatoEndereco } = cliente;
                clienteContatoEndereco.logradouro = e.target.value;
                setCliente({
                  ...cliente,
                  clienteContatoEndereco: { ...clienteContatoEndereco },
                });
              }}
            />
          </div>

          <div className="p-col-3 p-md-2 p-lg-2 p-fluid">
            <LabelSp>Nr.</LabelSp>
            <InputTextSp
              maxLength={10}
              required
              value={cliente.clienteContatoEndereco?.numero}
              disabled={viewMode() || !permissaoAdminAlteracao()}
              onChange={e => {
                const { clienteContatoEndereco } = cliente;
                clienteContatoEndereco.numero = e.target.value;
                setCliente({
                  ...cliente,
                  clienteContatoEndereco: { ...clienteContatoEndereco },
                });
              }}
            />
          </div>

          <div className="p-col-12 p-md-3 p-lg-3 p-fluid">
            <LabelSp>Bairro</LabelSp>
            <InputTextSp
              name="txtBairro"
              required
              value={cliente.clienteContatoEndereco?.bairro}
              disabled={viewMode() || !permissaoAdminAlteracao()}
              maxLength={100}
              onChange={e => {
                const { clienteContatoEndereco } = cliente;
                clienteContatoEndereco.bairro = e.target.value;
                setCliente({
                  ...cliente,
                  clienteContatoEndereco: { ...clienteContatoEndereco },
                });
              }}
            />
          </div>

          <div className="p-col-3 p-md-2 p-lg-2 p-fluid">
            <LabelSp>UF</LabelSp>
            <DropdownSp
              options={listaUfs}
              disabled={viewMode() || !permissaoAdminAlteracao()}
              value={ufSelecionada}
              onChange={e => {
                setUfSelecionada(e.value);
              }}
            />
          </div>

          <div className="p-col-9 p-md-5 p-lg-5 p-fluid">
            <LabelSp>Município</LabelSp>
            <DropDownLazy
              autoLoad
              placeholder="Selecione"
              required
              value={municipioSelecionado}
              onChange={e => {
                setMunicipioSelecionado(e);

                const municipio = e ? { id: e.value, nome: e.label } : null;

                const { clienteContatoEndereco } = cliente;

                clienteContatoEndereco.idMunicipio = e?.value || undefined;
                clienteContatoEndereco.municipio = municipio;
                setCliente({
                  ...cliente,
                  clienteContatoEndereco: { ...clienteContatoEndereco },
                });
              }}
              onFilter={async txtFilter => {
                const retorno = await loadMunicipio(txtFilter, ufSelecionada);
                return retorno;
              }}
              disabled={viewMode() || !permissaoAdminAlteracao()}
            />
          </div>

          <div className="p-col-12 p-md-5 p-lg-5 p-fluid">
            <LabelSp>e-mail</LabelSp>
            <InputTextSp
              name="txtEmail"
              value={cliente.clienteContatoEndereco?.email}
              onChange={e => {
                const { clienteContatoEndereco } = cliente;
                clienteContatoEndereco.email = e.target.value;
                setCliente({
                  ...cliente,
                  clienteContatoEndereco: { ...clienteContatoEndereco },
                });
              }}
              required
              disabled={viewMode() || !permissaoAdminAlteracao()}
            />
          </div>

          <div className="p-col-4 p-md-4 p-lg-4 p-fluid">
            <LabelSp>Telefone</LabelSp>
            <InputMaskSp
              mask="(99)9999-9999"
              value={cliente.clienteContatoEndereco?.fone}
              onChange={e => {
                const { clienteContatoEndereco } = cliente;
                clienteContatoEndereco.fone = e.target.value;
                setCliente({
                  ...cliente,
                  clienteContatoEndereco: { ...clienteContatoEndereco },
                });
              }}
              disabled={viewMode() || !permissaoAdminAlteracao()}
            />
          </div>

          <div className="p-col-4 p-md-4 p-lg-4 p-fluid">
            <LabelSp>Celular</LabelSp>
            <InputMaskSp
              mask="(99)99999-9999"
              value={cliente.clienteContatoEndereco?.celular}
              onChange={e => {
                const { clienteContatoEndereco } = cliente;
                clienteContatoEndereco.celular = e.target.value;
                setCliente({
                  ...cliente,
                  clienteContatoEndereco: { ...clienteContatoEndereco },
                });
              }}
              disabled={viewMode()}
            />
          </div>

          <div className="p-col-4 p-md-4 p-lg-4 p-fluid">
            <LabelSp>WhatsApp Finc.</LabelSp>
            <InputMaskSp
              mask="(99)99999-9999"
              value={cliente.clienteContatoEndereco?.whatsappFinanceiro}
              onChange={e => {
                const { clienteContatoEndereco } = cliente;
                clienteContatoEndereco.whatsappFinanceiro = e.target.value;
                setCliente({
                  ...cliente,
                  clienteContatoEndereco: { ...clienteContatoEndereco },
                });
              }}
              disabled={viewMode() || !permissaoAdminAlteracao()}
            />
          </div>

          <div className="p-col-12 p-md-4 p-lg-4 p-fluid">
            <LabelSp>Nome Contato</LabelSp>
            <InputTextSp
              required
              value={cliente.clienteContatoEndereco?.nomeContato}
              onChange={e => {
                const { clienteContatoEndereco } = cliente;
                clienteContatoEndereco.nomeContato = e.target.value;
                setCliente({
                  ...cliente,
                  clienteContatoEndereco: { ...clienteContatoEndereco },
                });
              }}
              disabled={viewMode() || !permissaoAdminAlteracao()}
            />
          </div>

          <div className="p-col-4 p-md-2 p-lg-2 p-fluid">
            <LabelSp>Inativo?</LabelSp>
            <DropdownSp
              disabled={viewMode() || !permissaoAdminAlteracao()}
              options={[
                { label: 'Sim', value: true },
                { label: 'Não', value: false },
              ]}
              value={cliente.inativo}
              onChange={e => {
                setCliente({
                  ...cliente,
                  inativo: e.target.value,
                  dataFimContrato: !e.target.value ? null : cliente.dataFimContrato,
                });
              }}
            />
          </div>

          <div className="p-col-8 p-md-2 p-lg-2 p-fluid">
            <LabelSp>Perfil Atualização</LabelSp>
            <DropdownSp
              disabled={viewMode() || !permissaoAdminAlteracao()}
              options={[
                { label: '1 - Piloto', value: 1 },
                { label: '2 - Geral', value: 2 },
                { label: '3 - Conservador', value: 3 },
              ]}
              value={Number(cliente.perfilAtualizacao) || 2}
              onChange={e => {
                setCliente({ ...cliente, perfilAtualizacao: e.target.value });
              }}
            />
          </div>

          <div className="p-col-12 p-md-4 p-lg-4 p-fluid">
            <LabelSp>Cliente Principal (quem paga)</LabelSp>
            <DropDownLazy
              autoLoad
              placeholder="Nenhum - este é o principal"
              showClear
              value={clientePrincipalSelecionado}
              onChange={e => {
                setClientePrincipalSelecionado(e);
                setCliente({
                  ...cliente,
                  idClientePrincipal: e ? e.value : null,
                });
              }}
              onFilter={async txtFilter => {
                const retorno = await loadClientePrincipal(txtFilter);
                return retorno;
              }}
              disabled={viewMode() || !permissaoAdminAlteracao()}
            />
          </div>

          <div className="p-col-12 p-md-12 p-lg-12 p-fluid">
            <LabelSp>Observação</LabelSp>
            <InputTextareaSp
              autoResize={false}
              value={cliente.observacao}
              maxLength={2000}
              rows={4}
              resize={false}
              onChange={e => {
                setCliente({
                  ...cliente,
                  observacao: e.target.value,
                });
              }}
              style={{ minHeight: 93 }}
              disabled={viewMode() || !permissaoAdminAlteracao}
            />
          </div>

          {renderSocios()}

          <div className="p-col-12 p-fluid">
            <hr style={{ marginTop: 3 }} />
          </div>

          {renderBotoes()}
        </>
      </>
    );
  }

  function renderBotoes() {
    return (
      <div className="p-col-12 p-lg-12" style={{ textAlign: 'start' }}>
        {!viewMode() ? (
          <ButtonSp
            className="p-button-success"
            icon="pi pi-save"
            label="Salvar"
            keyAccess="S"
            disabled={!permissaoAdminAlteracao()}
            onClick={handleSave}
          />
        ) : null}
        <ButtonSp
          className="p-button-secondary"
          label="Voltar"
          keyAccess="V"
          icon="pi pi-chevron-circle-left"
          onClick={handleBack}
        />
      </div>
    );
  }

  function renderContrato() {
    return (
      <>
        <ContratoOrcamento
          orcamento={false}
          contrato={{ ...cliente }}
          permissaoAdminAlteracao={permissaoAdminAlteracao}
          permissaoModulos={permissaoModulos}
          setContrato={setCliente}
          viewMode={viewMode}
          dispositivosUsados={dispositivosUsados}
        />
        {renderBotoes()}
      </>
    );
  }

  function renderSocios() {
    return (
      <>
        <div className="p-col-12 p-md-12 p-lg-12 p-fluid" style={{ margin: 0, padding: 0 }}>
          <div className="p-grid" style={{ margin: 0, padding: 0 }}>
            <div className="p-col-12 p-fluid">
              <LabelSp>Sócio</LabelSp>
              <hr style={{ marginTop: 3 }} />
            </div>

            <div className="p-col-12 p-md-8 p-lg-8 p-fluid">
              <LabelSp>Nome do Sócio</LabelSp>
              <InputTextSp
                text="text"
                value={clienteSocioEdicao?.nome}
                onChange={e => {
                  setClienteSocioEdicao({ ...clienteSocioEdicao, nome: e.target.value });
                }}
                disabled={viewMode() || !permissaoAdminAlteracao()}
              />
            </div>

            <div className="p-col-12 p-md-4 p-lg-4 p-fluid">
              <LabelSp>CPF</LabelSp>
              <InputMaskSp
                mask="999.999.999-99"
                value={clienteSocioEdicao?.cpf}
                onChange={e => {
                  setClienteSocioEdicao({ ...clienteSocioEdicao, cpf: e.target.value });
                }}
                disabled={viewMode() || !permissaoAdminAlteracao()}
              />
            </div>

            <div className="p-col-6 p-md-3 p-lg-3 p-fluid">
              <LabelSp>Celular</LabelSp>
              <InputMaskSp
                mask="(99)99999-9999"
                value={clienteSocioEdicao?.celular1}
                onChange={e => {
                  setClienteSocioEdicao({ ...clienteSocioEdicao, celular1: e.target.value });
                }}
                disabled={viewMode() || !permissaoAdminAlteracao()}
              />
            </div>

            <div className="p-col-6 p-md-3 p-lg-3 p-fluid">
              <LabelSp>Whatsapp</LabelSp>
              <InputMaskSp
                mask="(99)99999-9999"
                value={clienteSocioEdicao?.celular2}
                onChange={e => {
                  setClienteSocioEdicao({ ...clienteSocioEdicao, celular2: e.target.value });
                }}
                disabled={viewMode() || !permissaoAdminAlteracao()}
              />
            </div>
            <div className="p-col-9 p-md-4 p-lg-4 p-fluid">
              <LabelSp>e-mail</LabelSp>
              <InputTextSp
                value={clienteSocioEdicao?.email}
                disabled={viewMode() || !permissaoAdminAlteracao()}
                onChange={e => {
                  setClienteSocioEdicao({ ...clienteSocioEdicao, email: e.target.value });
                }}
              />
            </div>
            <div className="p-col-3 p-md-2 p-lg-2" style={{ textAlign: 'start' }}>
              <LabelSp style={{ display: 'block' }}>&#160;</LabelSp>
              <ButtonSp
                icon="pi pi-save"
                title="Salvar"
                onClick={salvarSocio}
                disabled={viewMode() || !permissaoAdminAlteracao()}
                type="button"
                className="p-button-success"
              />

              <ButtonSp
                onClick={novoSocio}
                type="button"
                disabled={viewMode() || !permissaoAdminAlteracao()}
                icon="pi pi-file"
                title="Novo"
              />
            </div>
          </div>

          <div className="p-col-12 p-fluid">
            <DataTableSp value={cliente.socios} style={{ marginBottom: '2px' }} responsive>
              <Column className="grid-col" header="Sócio" body={rowData => rowData.nome} />
              <Column className="grid-col" header="e-mail" body={rowData => rowData.email} />

              <Column
                className="grid-col-val"
                style={{ width: 105 }}
                header="CPF"
                body={rowData => rowData.cpf}
              />

              <Column
                className="grid-col-val"
                style={{ width: 108 }}
                header="Celular"
                body={rowData => rowData.celular1}
              />

              <Column
                className="grid-col-val"
                style={{ width: 108 }}
                header="Whatsapp"
                body={rowData => rowData.celular2}
              />

              <Column
                className="gid-col-acoes-35"
                bodyStyle={{ textAlign: 'end' }}
                body={renderButtonOpSocio}
              />
            </DataTableSp>
          </div>
        </div>
      </>
    );
  }

  function renderButtonOpSocio(rowData) {
    return (
      <BotaoMenuGrid
        handles={[
          () => {
            alterarSocio(rowData);
          },
          () => {
            excluirSocio(rowData);
          },
        ]}
        labels={['Alterar', 'Excluir']}
        icons={['pi pi-pencil', 'pi pi-trash']}
        disableds={[
          viewMode() || !permissaoAdminAlteracao(),
          viewMode() || !permissaoAdminAlteracao(),
        ]}
      />
    );
  }
}

export default ClienteCrud;
